import React from 'react';

import initial from 'lodash/initial';
import pick from 'lodash/pick';
import moment, {Moment, MomentInput} from 'moment-tz';
import RRule, {rrulestr} from 'rrule';

import {scrollIntoNode, scrollIntoStep} from 'app/steps/utils';
import {gt, ngt} from 'app/utils/i18n';
import {toOxfordCommaList} from 'app/utils/strings';

import type {ProcedureTaskCore, ProcessTaskCore} from 'sweet-taskinfo';
import type {
  SlimTaskInstanceSerializedType,
  TaskInstanceSerializedType,
  TaskMineDashboardSerializedType,
} from 'sweet-types';

// taken from rrule/nlp/i18n.ts
export interface Language {
  dayNames: string[];
  monthNames: string[];
  tokens: {
    [k: string]: RegExp;
  };
}

export const localiseDate = (date: MomentInput, tz: string): Moment =>
  moment(date).tz(tz);

export const getDueInTz = (
  task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType,
  tz: string
): Moment => localiseDate(task.due, tz);

export const rruleFromString = (rruleString: string): RRule => {
  return rrulestr(
    rruleString.toUpperCase().replace(/;UNTIL=([\dT]+)Z?/g, ';UNTIL=$1Z')
  );
};

const getMonthlyByWeekDayDescription = (rule: RRule) => {
  const opts = rule.options;
  const localeFuncs = moment.localeData();
  const localWeekDays = localeFuncs.weekdays();
  const interval = opts.interval === 1 ? '' : opts.interval;

  let daynames = toOxfordCommaList(
    opts.byweekday.map((d) => localWeekDays[(d + 1) % 7]),
    gt('or')
  );

  const monthweek =
    opts.bysetpos[0] > 0 ? localeFuncs.ordinal(opts.bysetpos[0]) : gt('last');

  if (byweekdayMatchesWorkDays(opts.byweekday)) {
    daynames = gt('weekday');
  }

  if (opts.bymonth != null) {
    let options = pick(rule.origOptions, ['freq', 'interval', 'bymonth']);
    rule = new RRule(options);
    let text = rule.toText(gt, getLang());
    return (
      text +
      ' ' +
      gt('on the %(monthweek)s %(daynames)s', {
        monthweek,
        daynames,
      })
    );
  } else {
    return gt('every %(interval)s month on the %(monthweek)s %(daynames)s', {
      interval,
      monthweek,
      daynames,
    });
  }
};

function getWeeklyWorkdaysForSomeMonthsDescription(rule: RRule): string {
  const opts = rule.options;
  const localeFuncs = moment.localeData();
  const localMonths = localeFuncs.months();

  let text = rule.toText(gt, getLang());
  const monthnames = opts.bymonth.map((m) => localMonths[m - 1]).join(', ');

  return `${text} ${gt('of %(monthnames)s', {monthnames})}`;
}

function byweekdayMatchesWorkDays(byweekday: number[]): boolean {
  return byweekday.join('') === '01234';
}

export const getLang = (): Language & {
  unused: Array<string>;
} => {
  // return an object with some translated text. Used by rrule.toText
  // this key is not used. It's here just to expose the text to
  // to be translated
  return {
    unused: [
      ngt('hour', 'hours', 1),
      ngt('day', 'days', 1),
      ngt('weekday', 'weekdays', 1),
      ngt('week', 'weeks', 1),
      ngt('year', 'years', 1),
      gt('every'),
      gt('until'),
      gt('for'),
      gt('times'),
      gt('time'),
      gt('(~ approximate)'),
      gt('hours'),
      gt('weekdays'),
      gt('days'),
      gt('in'),
      gt('weeks'),
      gt('on'),
      gt('months'),
      gt('years'),
      gt('on the'),
      gt('and'),
      gt('or'),
      gt('the'),
      gt('at'),
      gt('last'),
      gt('st'),
      gt('nd'),
      gt('rd'),
      gt('th'),
    ],
    dayNames: [
      gt('Sunday'),
      gt('Monday'),
      gt('Tuesday'),
      gt('Wednesday'),
      gt('Thursday'),
      gt('Friday'),
      gt('Saturday'),
    ],
    monthNames: [
      gt('January'),
      gt('February'),
      gt('March'),
      gt('April'),
      gt('May'),
      gt('June'),
      gt('July'),
      gt('August'),
      gt('September'),
      gt('October'),
      gt('November'),
      gt('December'),
    ],
    tokens: {},
  };
};
export const getRepetitionLabel = (strRRule: string, rrule?: RRule): string => {
  rrule = rrule ?? rruleFromString(strRRule);

  if (rrule.origOptions.count === 1) {
    return gt('Once');
  }

  let opts = rrule.options,
    byweekdayIsDefined = opts.byweekday?.length > 0,
    bymonthdayIsDefined = opts.bymonthday?.length > 0,
    bymonthIsDefined = opts.bymonth?.length > 0;

  if (bymonthdayIsDefined) {
    // rrule.js can't handle our usage of bymonthday.
    let options = pick(rrule.origOptions, [
      'freq',
      'bymonthday',
      'interval',
      'bysetpos',
      'bymonth',
      'until',
    ]);
    options.bymonthday =
      typeof options.bymonthday === 'number'
        ? initial([options.bymonthday])
        : initial(options.bymonthday);
    rrule = new RRule(options);
  }

  if (opts.freq === RRule.MONTHLY && byweekdayIsDefined) {
    // rrule.js can't handle our usage of byweekday.
    return getMonthlyByWeekDayDescription(rrule);
  }
  if (
    opts.freq === RRule.WEEKLY &&
    bymonthIsDefined &&
    byweekdayIsDefined &&
    byweekdayMatchesWorkDays(opts.byweekday)
  ) {
    // rrule.js can't handle our usage of freq=weekly + byweekday with workdays + bymonth
    return getWeeklyWorkdaysForSomeMonthsDescription(rrule);
  }

  return rrule.toText(gt, getLang());
};

export function isOverdue(
  task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType
): boolean {
  return task.completed != null ? false : moment().isAfter(moment(task.due));
}
export const repeats = (rrule: string): boolean =>
  rruleFromString(rrule).origOptions.count !== 1;

export const simpleGetProgressLabel = (
  task: SlimTaskInstanceSerializedType | TaskInstanceSerializedType
): {
  text: string;
  style: string;
} => {
  let style, text;

  if (task.completed != null) {
    return {
      text: gt('Completed'),
      style: 'success',
    };
  }

  if (task.progress_percent === 0) {
    if (task.started_at == null) {
      [text, style] = [gt('Not started'), 'secondary'];
    } else {
      [text, style] = [gt('Started'), 'info'];
    }
  } else if (task.progress_percent === 100) {
    [text, style] = [gt('Not yet marked complete'), 'info'];
  } else {
    [text, style] =
      task.progress_percent == null
        ? [gt('Started'), 'secondary']
        : [gt('%s%% complete', Math.round(task.progress_percent)), 'info'];
  }

  if (isOverdue(task)) {
    style = 'danger';
  }

  return {
    text,
    style,
  };
};

export function getActiveStep(
  taskCore: ProcedureTaskCore | ProcessTaskCore
): string | null {
  if (taskCore.hasVersion === true) {
    if (taskCore.state.progressInfo.isSequenced) {
      const activeNodes = [
        ...Array.from(taskCore.state.progressInfo.activeNodes),
      ];

      if (activeNodes.length > 0) {
        return activeNodes[0];
      }
    }
  }

  return null;
}

export function generateTemplateId(
  task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType
): string {
  return `${task.template.id}-${new Date(task.due).getTime() / 1000}`;
}

export function extractTaskInstanceId(
  task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType
): string {
  // task can be a concrete or ghost instance
  // let's extract real id or ghost id
  return task.id != null ? task.id.toString() : generateTemplateId(task);
}

// auxiliary func to make it easier to work with my-tasks response format
export function myTasksFlattened(
  myTasks: TaskMineDashboardSerializedType
): Array<SlimTaskInstanceSerializedType> {
  let tasks: Array<SlimTaskInstanceSerializedType> = [];

  for (const key in myTasks) {
    myTasks[key as keyof typeof myTasks].forEach((t) => tasks.push(t));
  }

  return tasks;
}

/**
 * Scrolls into the next active step of a task (the one to be checked off)
 * Do nothing if the document steps are not sequenced
 */
export function useScrollIntoActiveStep(
  taskCore: ProcedureTaskCore | ProcessTaskCore | null
) {
  const isMounted = React.useRef(false);

  const activeStep = React.useMemo(
    () => (taskCore == null ? null : getActiveStep(taskCore)),
    [taskCore?.task.progress]
  );

  React.useEffect(() => {
    if (activeStep != null && isMounted.current) {
      scrollIntoNode(activeStep);
    }
  }, [activeStep]);

  React.useEffect(() => {
    isMounted.current = taskCore != null;
  }, [taskCore != null]);
}

/**
 * After mounting, scrolls into the step which ID is in the URL hash, if any
 */
export function useScrollIntoStepInHashOnMount(
  taskCore: ProcedureTaskCore | ProcessTaskCore | null
) {
  React.useEffect(() => {
    const currentStepHash = window.location.hash.replace('#', '');

    if (taskCore != null && currentStepHash != null) {
      setTimeout(() => scrollIntoStep(currentStepHash), 250);
    }
  }, [taskCore?.task.id]);
}
